import { Group, useGroups } from "@/api/groups";
import {
  isOrganization,
  isOrganizationUser,
  RelationshipWithResource,
  useCreateRelationship,
  useDeleteRelationship,
  useListRelationshipsWithResources,
} from "@/api/relations";
import { useUserInfo } from "@/api/user";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils";
import {
  Relationship,
  ResourceObjectType,
  SubjectObjectType,
} from "@cohesive/api-client";
import { useEffect, useMemo, useRef, useState } from "react";

import { useUsers } from "@/api/users";
import { PERMISSIONS } from "@/constants/permissions";
import { useHasPermission } from "@/hooks/usePermissions";
import analytics from "@/utils/analytics";
import { Organization, OrganizationUser } from "@cohesive/api-client";
import { MetadataIcon, ResourceIcon } from "@cohesive/ui-components";
import { useIsMutating } from "@tanstack/react-query";
import { Link2, X } from "lucide-react";
import toast from "react-hot-toast";
import { ScrollArea } from "../ui/scroll-area";

interface ShareDialogProps {
  resourceObjectType: ResourceObjectType;
  resourceObjectId: string;
  subjectObjectType: SubjectObjectType[];
  title?: string;
  /** If provided, renders the dialog in controlled mode without a trigger. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Trigger element. Required in uncontrolled mode. */
  children?: React.ReactNode;
  /**
   * When set, shows a "Copy link" action in the dialog footer. Kept inside the
   * dialog (not as a standalone action) because the link only resolves for
   * users the resource has been shared with.
   */
  shareLinkUrl?: string;
}

interface SearchResult {
  id: string;
  name: string;
  type: "organization" | "organization_user" | "group";
  subject_relation?: Relationship["subject_relation"];
  email?: string;
  icon?: MetadataIcon | null;
  organizationId?: string; // For organization items, keep original ID for API calls
}

function relationshipKey(r: Relationship) {
  return `${r.resource_object_type}-${r.resource_object_id}-${r.subject_object_type}-${r.subject_object_id}-${r.subject_relation}`;
}

type RelationshipChangeCreate = {
  type: "add";
  data: Relationship;
};

type RelationshipChangeUpdate = {
  type: "update";
  previous: Relationship;
  next: Relationship;
};

type RelationshipChangeDelete = {
  type: "remove";
  data: Relationship;
};

type RelationshipChange =
  | RelationshipChangeCreate
  | RelationshipChangeUpdate
  | RelationshipChangeDelete;

// Add new type for relationship state tracking
export function ShareDialog({
  resourceObjectType,
  resourceObjectId,
  subjectObjectType,
  title = "Share",
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  children,
  shareLinkUrl,
}: ShareDialogProps) {
  const isOrgAdmin = useHasPermission(PERMISSIONS.ORG_ADMIN.ALL);

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    controlledOnOpenChange?.(next);
  };
  const [searchQuery, setSearchQuery] = useState("");

  const [popoverOpen, setPopoverOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [currentState, setCurrentState] = useState<Relationship[]>([]);
  // True only once the open-triggered refetch has seeded `currentState` from the
  // server. Until then `currentState` is empty while the cached `relationships`
  // baseline still lists existing shares, so the diff would read as "remove
  // everything" — gating Save on this prevents a stray click in that window from
  // mass-unsharing. A deliberate remove-all still works (initialized stays true).
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!open) {
      setCurrentState([]); // Clear changes when dialog is closed
      setSearchQuery(""); // Clear search query when dialog is closed
      setInitialized(false);
    }
  }, [open]);

  // Fetch data from different sources
  const { data: userInfo } = useUserInfo();
  const { data: groups = [] } = useGroups({
    enabled: isOrgAdmin,
  });
  const { data: users = [] } = useUsers();

  // Gate the fetch on `open`: ShareDialog is mounted-but-closed for every row in
  // list contexts (e.g. the Files rail), and an ungated query fired one relationships
  // request per record on render. The dialog only needs this data once opened; the
  // open-effect below refetches then. Kills the N+1 wherever a closed dialog mounts.
  const { data: relationships = [], refetch: refetchRelationships } =
    useListRelationshipsWithResources(resourceObjectType, resourceObjectId, {
      enabled: open,
    });

  useEffect(() => {
    if (open) {
      refetchRelationships().then(({ data = [] }) => {
        setCurrentState(data);
        setInitialized(true);
      });
    }
  }, [open]);

  const createRelationship = useCreateRelationship();
  const deleteRelationship = useDeleteRelationship();

  // Set initial state when relationships load
  useEffect(() => {
    if (relationships) {
      setCurrentState(relationships);
    }
  }, []);

  // Calculate changes by diffing states
  const calculateChanges = useMemo(() => {
    if (!relationships) return [];

    const initialState = relationships;

    const changes: RelationshipChange[] = [];

    // Find additions and updates
    currentState.forEach((current) => {
      const initial = initialState.find(
        (rel) =>
          rel.subject_object_type === current.subject_object_type &&
          rel.subject_object_id === current.subject_object_id &&
          rel.subject_relation === current.subject_relation,
      );

      if (!initial) {
        // Addition
        changes.push({
          type: "add",
          data: current,
        });
      } else if (initial.relation !== current.relation) {
        // Update
        changes.push({
          type: "update",
          previous: initial,
          next: current,
        });
      }
    });

    // Find removals
    initialState.forEach((initial) => {
      const exists = currentState.some(
        (current) =>
          current.subject_object_type === initial.subject_object_type &&
          current.subject_object_id === initial.subject_object_id &&
          current.subject_relation === initial.subject_relation,
      );

      if (!exists) {
        changes.push({
          type: "remove",
          data: initial,
        });
      }
    });

    return changes;
  }, [currentState, relationships, resourceObjectType, resourceObjectId]);

  // Combine and format all searchable items
  const searchableItems = useMemo(() => {
    const items: SearchResult[] = [];

    // Add organization if applicable
    if (subjectObjectType.includes("organization") && userInfo) {
      items.push({
        id: `${userInfo.organization.id}-member`,
        name: "All Organization Members",
        type: "organization",
        subject_relation: "member",
        organizationId: userInfo.organization.id, // Keep original ID for API calls
      });

      items.push({
        id: `${userInfo.organization.id}-admin`,
        name: "All Organization Admins",
        type: "organization",
        subject_relation: "admin",
        organizationId: userInfo.organization.id, // Keep original ID for API calls
      });
    }

    // Add users if applicable
    if (subjectObjectType.includes("organization_user") && userInfo) {
      users?.forEach((user) => {
        items.push({
          id: user.id,
          name: user.name,
          email: user.email,
          type: "organization_user",
        });
      });
    }

    // Add groups if applicable
    if (subjectObjectType.includes("group")) {
      groups?.forEach((group: Group) => {
        items.push({
          id: group.id,
          name: group.name,
          type: "group",
        });
      });
    }

    return items;
  }, [userInfo, groups, subjectObjectType]);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery) return searchableItems;

    return searchableItems.filter(
      (item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.email?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchableItems, searchQuery]);

  const commandGroups = useMemo(() => {
    return filteredItems.reduce(
      (acc, item) => {
        (acc[item.type] = acc[item.type] || []).push(item);
        return acc;
      },
      {} as Record<string, SearchResult[]>,
    );
  }, [filteredItems]);

  // Update handleItemSelect to track additions
  const handleItemSelect = (item: SearchResult) => {
    let subjectRelation: Relationship["subject_relation"];

    if (item.subject_relation) {
      subjectRelation = item.subject_relation;
    } else {
      if (resourceObjectType === "skill") {
        if (item.type === "organization") {
          subjectRelation = "member";
        }

        if (item.type === "group") {
          subjectRelation = "member";
        }
      }

      if (resourceObjectType === "folder") {
        if (item.type === "organization") {
          subjectRelation = "member";
        }

        if (item.type === "group") {
          subjectRelation = "member";
        }
      }

      if (resourceObjectType === "canvas") {
        if (item.type === "organization") {
          subjectRelation = "member";
        }

        if (item.type === "group") {
          subjectRelation = "member";
        }
      }

      if (resourceObjectType === "file_record") {
        if (item.type === "organization") {
          subjectRelation = "member";
        }

        if (item.type === "group") {
          subjectRelation = "member";
        }
      }
    }

    const newRelationship: RelationshipWithResource = {
      resource_object_type: resourceObjectType,
      resource_object_id: resourceObjectId,
      relation: "viewer",
      subject_object_type: item.type,
      subject_object_id: item.organizationId || item.id, // Use organizationId for organization items
      subject_relation: subjectRelation,
    };
    setCurrentState((prev) => [...prev, newRelationship]);
    setSearchQuery("");
    setPopoverOpen(false);
  };

  // Update handleRelationChange to track updates
  const handleRelationChange = (relationship: Relationship) => {
    setCurrentState((prev) => [
      ...prev.filter(
        (r) => relationshipKey(r) !== relationshipKey(relationship),
      ),
      relationship,
    ]);
  };

  // Update handleRemoveItem to track removals
  const handleRemoveItem = (item: RelationshipWithResource) => {
    if (!item || item.relation === "owner") return;

    setCurrentState((prev) =>
      prev.filter((r) => relationshipKey(r) !== relationshipKey(item)),
    );
  };

  // Add a mapping function for display names
  const getDisplayName = (type: string) => {
    const mapping: Record<string, string> = {
      organization: "Your Organization",
      user: "Users",
      organization_user: "Users",
      group: "Groups",
      folder: "Folders",
    };
    return mapping[type] || type;
  };

  // Create dynamic placeholder text
  const sharables = useMemo(() => {
    const types = subjectObjectType
      .map(getDisplayName)
      .map((s) => s.toLowerCase());
    return types.length > 1
      ? `${types.slice(0, -1).join(", ")}${types.length > 2 ? "," : ""} or ${types[types.length - 1]}`
      : `${types[0]}`;
  }, [subjectObjectType]);

  const isCreatingRelationships = useIsMutating({
    mutationKey: ["createRelationship"],
  });

  const isDeletingRelationships = useIsMutating({
    mutationKey: ["deleteRelationship"],
  });

  const isMutating = isCreatingRelationships > 0 || isDeletingRelationships > 0;

  const getRelationshipResource = (
    objectType: string,
    objectId: string,
  ): Group | OrganizationUser | Organization | undefined => {
    switch (objectType) {
      case "organization":
        return userInfo?.organization;
      case "organization_user":
        return users.find((user) => user.id === objectId);
      case "group":
        return groups.find((group) => group.id === objectId);
      default:
        return undefined;
    }
  };

  // Update handleShare to use calculated changes
  const handleShare = async () => {
    try {
      let addedCount = 0;
      let removedCount = 0;
      let updatedCount = 0;

      await Promise.all(
        calculateChanges.map(async (change) => {
          switch (change.type) {
            case "add":
              await createRelationship.mutateAsync(change.data);

              analytics.trackRelationshipCreated({
                relationship: change.data,
                resource: getRelationshipResource(
                  change.data.resource_object_type,
                  change.data.resource_object_id,
                ),
                subject: getRelationshipResource(
                  change.data.subject_object_type,
                  change.data.subject_object_id,
                ),
              });

              addedCount++;
              break;
            case "update":
              await deleteRelationship.mutateAsync(change.previous);
              await createRelationship.mutateAsync(change.next);

              analytics.trackRelationshipUpdated({
                relationship: change.next,
                resource: getRelationshipResource(
                  change.next.resource_object_type,
                  change.next.resource_object_id,
                ),
                subject: getRelationshipResource(
                  change.next.subject_object_type,
                  change.next.subject_object_id,
                ),
              });

              updatedCount++;
              break;
            case "remove":
              await deleteRelationship.mutateAsync(change.data);

              analytics.trackRelationshipDeleted({
                relationship: change.data,
                resource: getRelationshipResource(
                  change.data.resource_object_type,
                  change.data.resource_object_id,
                ),
                subject: getRelationshipResource(
                  change.data.subject_object_type,
                  change.data.subject_object_id,
                ),
              });

              removedCount++;
              break;
          }
        }),
      );
      // Create toast message based on actual changes
      let description = "";
      const parts = [];

      if (addedCount > 0) {
        parts.push(`${addedCount} added`);
      }
      if (removedCount > 0) {
        parts.push(`${removedCount} removed`);
      }
      if (updatedCount > 0) {
        parts.push(`${updatedCount} updated`);
      }

      description = parts.join(", ");

      toast.success(`Changes saved${description ? `: ${description}` : ""}`);

      setOpen(false);
    } catch (error) {
      toast.error(
        "There was a problem updating sharing permissions. Please try again.",
      );
    }
  };

  // Add a helper function to determine sort order
  const getTypeOrder = (type: string): number => {
    const order = {
      "Your Organization": 0,
      Groups: 1,
      Users: 2,
      Agents: 3,
      Folders: 4,
    } as const;
    return order[type as keyof typeof order] ?? 999;
  };

  // Update the groupedSelectedItems memo with new sorting
  const groupedSelectedItems = useMemo(() => {
    // Workspace-scoped relations (canvas#workspace) are persisted on the backend
    // to grant access via workspace membership, but must not appear in the share
    // UI. They remain in `currentState` so the save diff treats them as unchanged.
    const visibleState = currentState.filter(
      (item) => (item.subject_object_type as string) !== "workspace",
    );

    // First, group items as before
    const grouped = visibleState.reduce(
      (acc, item) => {
        const groupName = getDisplayName(item.subject_object_type);
        const itemWithResource = {
          ...item,
          resource: getRelationshipResource(
            item.subject_object_type,
            item.subject_object_id,
          ),
        };

        if (!acc[groupName]) {
          acc[groupName] = [];
        }
        acc[groupName]?.push(itemWithResource);
        return acc;
      },
      {} as Record<string, RelationshipWithResource[]>,
    );

    // Sort items within each group by name
    Object.keys(grouped).forEach((key) => {
      grouped[key]?.sort((a, b) => {
        const nameA = a.resource?.name?.toLowerCase() ?? "";
        const nameB = b.resource?.name?.toLowerCase() ?? "";
        return nameA.localeCompare(nameB);
      });
    });

    // Return as a sorted object based on type order
    return Object.keys(grouped)
      .sort((a, b) => getTypeOrder(a) - getTypeOrder(b))
      .reduce(
        (obj, key) => {
          obj[key] = grouped[key] ?? [];
          return obj;
        },
        {} as Record<string, RelationshipWithResource[]>,
      );
  }, [currentState]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      {/** `overflow-visible` lets the inline (`disablePortal`) sharing
       *   popover paint outside the dialog's bounds. The default
       *   DialogContent has `max-h-[90vh] overflow-y-auto`, which
       *   (per CSS spec) also forces `overflow-x: auto` and clips the
       *   popover via the dialog's transform-created containing block.
       *   The inner ScrollArea (`max-h-[calc(100vh-300px)]`) still
       *   keeps the people-with-access list bounded, so removing the
       *   dialog's own scroll is safe. */}
      <DialogContent className="grid-cols-[minmax(0,1fr)] overflow-visible">
        <DialogHeader className="min-w-0">
          <DialogTitle className="break-words" title={title}>
            {title}
          </DialogTitle>
          <DialogDescription>
            Select {sharables} to share this resource with.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(100vh-300px)]">
          <div className="space-y-4">
            <div className="flex flex-col gap-4">
              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    ref={buttonRef}
                    variant="outline"
                    role="combobox"
                    aria-expanded={popoverOpen}
                    className="w-full justify-start text-left font-normal"
                  >
                    <span className="text-muted-foreground">
                      Select {sharables}...
                    </span>
                  </Button>
                </PopoverTrigger>
                {/* `disablePortal` keeps the popover inside the parent
                 *  Dialog's DOM tree. Portalling it to body makes the
                 *  Dialog's focus scope yank focus back out of
                 *  CommandInput (typing breaks) and, with `modal`,
                 *  causes the popover to slap aria-hidden on the
                 *  dialog content. Inline rendering sidesteps both. */}
                <PopoverContent
                  disablePortal
                  className="p-0"
                  style={{ width: buttonRef.current?.offsetWidth }}
                  align="start"
                >
                  <Command>
                    <CommandInput
                      placeholder={`Search ${sharables}...`}
                      onValueChange={setSearchQuery}
                    />
                    <CommandList>
                      <CommandEmpty>No results found.</CommandEmpty>
                      {Object.entries(commandGroups)?.map(([type, items]) => (
                        <CommandGroup key={type} heading={getDisplayName(type)}>
                          {items.map((item) => (
                            <CommandItem
                              key={item.id}
                              onSelect={() => {
                                handleItemSelect(item);
                                setPopoverOpen(false);
                              }}
                              disabled={currentState.some(
                                (selectedItem) =>
                                  selectedItem.subject_object_id === item.id &&
                                  selectedItem.subject_object_type ===
                                    item.type &&
                                  selectedItem.subject_relation ===
                                    item.subject_relation,
                              )}
                              className={cn(
                                "cursor-pointer",
                                currentState.some(
                                  (selectedItem) =>
                                    selectedItem.subject_object_id ===
                                      item.id &&
                                    selectedItem.subject_object_type ===
                                      item.type &&
                                    selectedItem.subject_relation ===
                                      item.subject_relation,
                                ) &&
                                  "opacity-50 cursor-not-allowed pointer-events-none",
                              )}
                            >
                              <span>
                                {item.email
                                  ? `${item.name} (${item.email})`
                                  : item.name}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <h3 className="text-md font-medium">People with access</h3>
              {currentState.length > 0 && (
                <div className="flex flex-col gap-4">
                  {Object.entries(groupedSelectedItems).map(
                    ([groupName, items]) => (
                      <div key={groupName} className="">
                        <h4 className="text-sm font-medium text-muted-foreground">
                          {groupName}
                        </h4>
                        <div className="flex flex-col gap-2">
                          {items.map((item) => {
                            let icon: MetadataIcon | undefined = undefined;

                            if (
                              isOrganizationUser(item.resource) &&
                              item.resource.picture_url
                            ) {
                              icon = {
                                type: "custom",
                                custom: {
                                  url: item.resource.picture_url,
                                },
                              };
                            } else if (
                              isOrganization(item.resource) &&
                              item.resource.logo_url
                            ) {
                              icon = {
                                type: "custom",
                                custom: {
                                  url: item.resource.logo_url,
                                },
                              };
                            } else if (
                              item?.resource &&
                              "icon" in item?.resource &&
                              (item?.resource?.icon as MetadataIcon)
                            ) {
                              icon = item.resource.icon as MetadataIcon;
                            }

                            return (
                              <div
                                key={relationshipKey(item)}
                                className="grid grid-cols-[auto_1fr_auto] items-center gap-4 rounded-md px-2 py-2"
                              >
                                <ResourceIcon
                                  type={item.subject_object_type}
                                  icon={icon}
                                  className="flex h-8 w-8 items-center justify-center rounded-full bg-muted"
                                />
                                <div className="flex flex-col min-w-0">
                                  <span className="font-medium truncate">
                                    {item.resource?.name}
                                  </span>
                                  {isOrganizationUser(item.resource) &&
                                    item?.resource?.email && (
                                      <span className="text-sm text-muted-foreground truncate">
                                        {item.resource.email}
                                      </span>
                                    )}

                                  {isOrganization(item.resource) && (
                                    <>
                                      {item.subject_relation === "member" ? (
                                        <span className="text-sm text-muted-foreground truncate">
                                          All members in your organization
                                        </span>
                                      ) : item.subject_relation === "admin" ? (
                                        <span className="text-sm text-muted-foreground truncate">
                                          All admins in your organization
                                        </span>
                                      ) : null}
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 justify-end">
                                  {item.relation === "owner" ? (
                                    <>
                                      <span className="w-[120px] text-sm text-muted-foreground capitalize text-left pl-3">
                                        {item.relation}
                                      </span>
                                      <div className="h-8 w-8" />
                                    </>
                                  ) : (
                                    <>
                                      <Select
                                        value={item.relation}
                                        onValueChange={(value) =>
                                          handleRelationChange({
                                            ...item,
                                            relation:
                                              value as Relationship["relation"],
                                          })
                                        }
                                      >
                                        <SelectTrigger className="w-[120px]">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="viewer">
                                            Viewer
                                          </SelectItem>
                                          <SelectItem value="editor">
                                            Editor
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => handleRemoveItem(item)}
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
          </div>
        </ScrollArea>

        <div className="mt-6 flex items-center justify-between gap-2">
          <div>
            {shareLinkUrl && (
              <Button
                variant="ghost"
                className="gap-2 text-muted-foreground"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(shareLinkUrl);
                    toast.success("Link copied to clipboard");
                  } catch {
                    toast.error("Failed to copy link");
                  }
                }}
              >
                <Link2 className="size-4" />
                Copy link
              </Button>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleShare}
              disabled={
                !initialized || calculateChanges.length === 0 || isMutating
              }
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
